import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Upload, X, Loader2, AlertCircle } from 'lucide-react';

const LOCATIONS = ['Accra', 'Kumasi', 'Tamale', 'Cape Coast', 'Takoradi', 'Tema', 'Ho', 'Sunyani', 'Bolgatanga', 'Koforidua'];
const TRANSMISSIONS = ['Automatic', 'Manual'];
const FUEL_TYPES = ['Petrol', 'Diesel', 'Hybrid', 'Electric'];
const CONDITIONS = ['Used', 'Certified Pre-Owned', 'New'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EditListing() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState({
    title: '',
    price: '',
    description: '',
    mileage: '',
    transmission: 'Automatic',
    fuel_type: 'Petrol',
    condition: 'Used',
    location: 'Accra',
    color: '',
  });
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    API.get(`/listings/${id}`).then(res => {
      const data = res.data;
      
      if (data.seller_id !== user.user_id) {
        toast.error('You can only edit your own listings');
        navigate('/dashboard');
        return;
      }
      
      setListing(data);
      setForm({
        title: data.title || '',
        price: data.price?.toString() || '',
        description: data.description || '',
        mileage: data.mileage?.toString() || '',
        transmission: data.transmission || 'Automatic',
        fuel_type: data.fuel_type || 'Petrol',
        condition: data.condition || 'Used',
        location: data.location || 'Accra',
        color: data.color || '',
      });
      setPhotos(data.photos || []);
      setLoading(false);
    }).catch(err => {
      toast.error('Failed to load listing');
      navigate('/dashboard');
    });
  }, [id, user, navigate]);

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 8) {
      toast.error('Maximum 8 photos');
      return;
    }
    
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await API.post('/upload', fd);
        setPhotos(p => [...p, res.data.url]);
        if (res.data.suspicious) {
          toast.warning('This image has been flagged as potentially suspicious');
        }
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.price || parseFloat(form.price) <= 0) {
      toast.error('Valid price is required');
      return;
    }
    
    setSaving(true);
    try {
      const updateData = {
        title: form.title.trim(),
        price: parseFloat(form.price),
        description: form.description.trim(),
        mileage: form.mileage ? parseInt(form.mileage) : null,
        transmission: form.transmission,
        fuel_type: form.fuel_type,
        condition: form.condition,
        location: form.location,
        color: form.color.trim(),
        photos: photos,
      };
      
      await API.put(`/listings/${id}`, updateData);
      toast.success('Listing updated successfully');
      navigate(`/listing/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update listing');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <main className="min-h-screen py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit Listing</h1>
            <p className="text-sm text-muted-foreground">
              {listing?.make} {listing?.model} {listing?.year}
            </p>
          </div>
        </div>

        {/* Cannot Edit Notice */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Some fields cannot be edited</p>
            <p className="text-muted-foreground">VIN, Make, Model, and Year are locked after listing creation to maintain trust score integrity.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
            <h2 className="font-semibold mb-4">Basic Information</h2>
            
            <div>
              <Label htmlFor="title">Listing Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="e.g., 2020 Toyota Corolla - Pristine Condition"
                className="mt-1"
                data-testid="edit-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price (GHS) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={form.price}
                  onChange={(e) => update('price', e.target.value)}
                  placeholder="85000"
                  className="mt-1"
                  data-testid="edit-price"
                />
              </div>
              <div>
                <Label htmlFor="mileage">Mileage (km)</Label>
                <Input
                  id="mileage"
                  type="number"
                  value={form.mileage}
                  onChange={(e) => update('mileage', e.target.value)}
                  placeholder="50000"
                  className="mt-1"
                  data-testid="edit-mileage"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Describe your car - condition, history, features..."
                className="mt-1 min-h-[100px]"
                data-testid="edit-description"
              />
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
            <h2 className="font-semibold mb-4">Vehicle Details</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Transmission</Label>
                <Select value={form.transmission} onValueChange={(v) => update('transmission', v)}>
                  <SelectTrigger className="mt-1" data-testid="edit-transmission">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSMISSIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fuel Type</Label>
                <Select value={form.fuel_type} onValueChange={(v) => update('fuel_type', v)}>
                  <SelectTrigger className="mt-1" data-testid="edit-fuel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_TYPES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condition</Label>
                <Select value={form.condition} onValueChange={(v) => update('condition', v)}>
                  <SelectTrigger className="mt-1" data-testid="edit-condition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <Select value={form.location} onValueChange={(v) => update('location', v)}>
                  <SelectTrigger className="mt-1" data-testid="edit-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="color">Color</Label>
              <Input
                id="color"
                value={form.color}
                onChange={(e) => update('color', e.target.value)}
                placeholder="e.g., Silver"
                className="mt-1"
                data-testid="edit-color"
              />
            </div>
          </div>

          {/* Photos */}
          <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
            <h2 className="font-semibold mb-4">Photos</h2>
            
            <label className={`flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:border-gold/50 transition-colors ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
              <Upload className="w-8 h-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {uploading ? 'Uploading...' : 'Click to upload more photos (max 8)'}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={uploadPhoto}
                className="hidden"
                disabled={uploading}
                data-testid="edit-photo-upload"
              />
            </label>

            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-4">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border/50 group">
                    <img
                      src={p.startsWith('http') ? p : `${BACKEND_URL}${p}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/80 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 bg-gold text-black text-[10px] px-1.5 py-0.5 rounded font-medium">
                        Main
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gold text-black hover:brightness-110 gap-2"
              disabled={saving}
              data-testid="save-listing-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
